import { cloneElement, PropsWithChildren, ReactNode, useCallback } from 'react';
import { ActionIcon, isElement, Tooltip } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';

import { useToast } from '@/hooks/useToast';
import { openapiClient } from '@/lib/openapi-fetch';
import type { FetchError } from '@/types/api';

type IssueBulkPatchPath =
  | '/issue/finished_intel/bulk'
  | '/issue/incidents/bulk'
  | '/issue/raw_intel/bulk'
  | '/issue/rfis/bulk'
  | '/issue/takedowns/bulk';

type BulkPatchItem = {
  id: number;
  body: {
    client_state: Array<Record<string, string>>;
  };
};

function useClientStateMutation(resource: string) {
  const { createToast } = useToast();
  const bulkPath = `${resource}/bulk` as IssueBulkPatchPath;

  return useMutation<unknown, FetchError, BulkPatchItem[]>({
    mutationFn: async (items) => {
      const { data, error } = await openapiClient.PATCH(bulkPath, {
        body: items as any,
      });
      if (error) throw new Error(JSON.stringify(error));
      return data;
    },
    onError: (error) => {
      createToast(error.message, 'error');
    },
  });
}

type ClientStateTriggerProps = IconProps | ChildrenProps;

interface BaseProps {
  resource: string;
  body: Record<string, string>;
  // [[id1, [client_id1, client_id2, ...]], [id2, [client_id1, client_id2, ...]]]
  updateIds: Array<[number, Array<string>]>;
  onSuccess?: VoidFunction;
  disabled?: boolean;
}

interface IconProps extends BaseProps {
  icon: ReactNode;
  label: string;
  color?: string;
  loading?: boolean;
  size?: number;
  children?: never; // This ensures children can't be used with icon
}

interface ChildrenProps extends BaseProps {
  icon?: never; // This ensures icon can't be used with children
  label?: never;
  color?: never;
  size?: never;
  loading?: never;
  children: ReactNode;
}

const ClientStateTrigger = ({
  resource,
  body,
  updateIds,
  onSuccess,
  children,
  icon,
  label,
  color,
  size = 20,
  loading,
  disabled,
}: PropsWithChildren<ClientStateTriggerProps>) => {
  const mutation = useClientStateMutation(resource);

  if (children && !isElement(children)) {
    throw new Error(
      // eslint-disable-next-line max-len
      'ClientStateTrigger component children should be an element or a component that accepts ref. Fragments, strings, numbers and other primitive values are not supported'
    );
  }
  // updateIds[0][1] = []

  const onClick = useCallback(() => {
    mutation.mutate(
      updateIds.map(([id, client_ids]) => ({
        id,
        body: {
          client_state: client_ids.map((client_id) => ({
            client_id,
            ...body,
          })),
        },
      })),
      {
        onSuccess,
      }
    );
  }, [body, updateIds]);

  return icon ? (
    <Tooltip label={label}>
      <ActionIcon
        onClick={onClick}
        size={size}
        variant="subtle"
        aria-label={label}
        loaderProps={{ size: size - 4 }}
        loading={loading || mutation.isPending}
        color={color}
        disabled={disabled}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  ) : (
    cloneElement(children as any, {
      onClick,
      loading: loading || mutation.isPending,
      disabled: disabled || mutation.isPending,
    })
  );
};

export default ClientStateTrigger;
